import React from "react";
import { Download, Filter, PackageOpen, ShieldCheck, Upload, Warehouse } from "lucide-react";
import { motion } from "framer-motion";
import PageHero from "../components/common/PageHero";
import StatGrid from "../components/common/StatGrid";
import DataTableCard from "../components/common/DataTableCard";

export default function Inventory() {
  return (
    <div className="erp-page-grid">
      <PageHero
        badge="Inventory control"
        title="Stock visibility redesigned for speed and trust."
        subtitle="Raw, WIP, finished, approval and movement views now use cleaner cards, lighter spacing and more professional emphasis."
        actions={
          <>
            <motion.button className="erp-btn erp-btn-ghost" whileTap={{ scale: 0.97 }}>
              <Filter size={15} /> Filters
            </motion.button>
            <motion.button className="erp-btn erp-btn-ghost" whileTap={{ scale: 0.97 }}>
              <Upload size={15} /> Import adjustments
            </motion.button>
            <motion.button className="erp-btn erp-btn-primary" whileTap={{ scale: 0.97 }}>
              <Download size={15} /> Export stock
            </motion.button>
          </>
        }
      />

      <StatGrid
        items={[
          {
            title: "Raw stock",
            value: "₹42.6L",
            meta: "Healthy intake cover",
            trend: "Stable",
            tone: "neutral",
            icon: Warehouse,
          },
          {
            title: "WIP",
            value: "₹18.2L",
            meta: "Distributed across core stages",
            trend: "Active",
            tone: "success",
            icon: PackageOpen,
          },
          {
            title: "Finished",
            value: "₹31.4L",
            meta: "Ready for dispatch and approval",
            trend: "Ready",
            tone: "success",
            icon: ShieldCheck,
          },
          {
            title: "Approval stock",
            value: "₹8.4L",
            meta: "Outside stock under watch",
            trend: "Review",
            tone: "warning",
            icon: Download,
          },
        ]}
      />

      <div className="erp-grid erp-grid-2">
        <section className="erp-card">
          <div className="erp-card-head">
            <div>
              <h3>Stock composition</h3>
              <p>Better visual split of where working capital is sitting.</p>
            </div>
          </div>
          <div className="erp-progress-stack">
            {[
              ["Raw materials", 44, "Largest capital share, supports continuity."],
              ["WIP stages", 21, "Controlled and moving on time."],
              ["Finished goods", 28, "Good readiness for dispatch."],
              ["Dead stock", 7, "Small but still worth commercial action."],
            ].map(([title, value, text]) => (
              <div key={title} className="erp-progress-card">
                <div className="erp-progress-top">
                  <strong>{title}</strong>
                  <span>{value}%</span>
                </div>
                <div className="erp-progress-line">
                  <span style={{ width: `${value}%` }} />
                </div>
                <p>{text}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="erp-card">
          <div className="erp-card-head">
            <div>
              <h3>Movement watch</h3>
              <p>Recent movement feed with a cleaner operational tone.</p>
            </div>
          </div>
          <div className="erp-list">
            {[
              ["Raw → Ghat", "Amethyst intake issued for next cycle."],
              ["MM → Final Assorting", "Two premium lots advanced this hour."],
              ["Approval → Finished", "Returned pieces re-entered into ready stock."],
            ].map(([title, text]) => (
              <div key={title} className="erp-list-row">
                <div className="erp-list-icon small" />
                <div>
                  <strong>{title}</strong>
                  <p>{text}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>

      <DataTableCard
        title="Stage-wise stock"
        subtitle="Monitor product availability, reserved quantity and approval exposure without clutter."
        columns={[
          { key: "product", label: "Product" },
          { key: "stage", label: "Stage" },
          { key: "available", label: "Available" },
          { key: "reserved", label: "Reserved" },
          { key: "approval", label: "Approval" },
        ]}
        rows={[
          {
            product: "Ruby Oval",
            stage: "Finished",
            available: "8,240 pcs",
            reserved: "1,200 pcs",
            approval: "980 pcs",
          },
          {
            product: "Amethyst Raw",
            stage: "Raw",
            available: "11.8 kg",
            reserved: "2.1 kg",
            approval: "0",
          },
          {
            product: "Emerald Pair",
            stage: "Approval Stock",
            available: "620 pcs",
            reserved: "110 pcs",
            approval: "620 pcs",
          },
        ]}
      />
    </div>
  );
}
